import { Injectable } from "@angular/core";
import environment from "../../environments/environment";
import { formatDate } from "@angular/common";
import {
  MigrationDbTables,
  MigrationPostProcessingStatus,
  MigrationStatus,
  MigrationSteps,
} from "@app/common/constants";
import {
  Company,
  GroupedMigrationMetrics,
  Migration,
  MigrationExecutionStep,
  MigrationExecutionStoredProcedure,
  MigrationMetric,
  MigrationStatisticsRow,
  Protocol,
} from "@app/interface/interfaces";
import { DialogService } from "./dialog.service";
import { Inject, LOCALE_ID } from "@angular/core";
import { MigrationStatusColorMap, OverallMetrics } from "@app/interface/models";
import { UtilsService } from "./utils.service";

@Injectable({
  providedIn: "root",
})
export class MigrationService {
  private apiUrl = environment.apiUrl;

  constructor(
    private dialogService: DialogService,
    private utilsService: UtilsService,
    @Inject(LOCALE_ID) private locale: string,
  ) {}

  /*Process records */

  processCompanyData(companiesData: Company[]) {
    let companies = companiesData;
    companies.forEach((company: Company) => {
      company.statusClass = this.getClassFromMigrationStatus(company.status);
    });
    return companies;
  }

  processProtocolsData(protocols: Protocol[]) {
    const cnx5Offset = 84;
    protocols.forEach((protocol: Protocol) => {
      protocol.createdOnLocal = this.getDateFromIsoString(
        protocol.created_dt as string,
        false,
      ).toString();
      protocol.irb_closure_dt_local = this.getDateFromIsoString(
        protocol.irb_closure_dt as string,
        false,
      ).toString();
      protocol.irb_approval_dt_local = this.getDateFromIsoString(
        protocol.irb_approval_dt as string,
        false,
      ).toString();
      protocol.cnx5ExternalReferenceId = protocol.cx5_study_id + cnx5Offset;
      this.updateEmptyFields(protocol);
    });
    return protocols;
  }

  processMigrationRecord(
    migration: Migration,
    shouldParseStatistics: boolean = true,
  ): Migration {
    migration.statistics_obj = shouldParseStatistics
      ? this.parseMigrationStatistics(migration.statistics)
      : migration.statistics_obj;

    // Calculate start and end times for duration computation
    let effectedStarted = migration.started || "";
    let effectiveEnded = "";

    // If backend provided duration (in milliseconds), use it directly
    // Otherwise calculate from statistics timestamps
    if (!migration.duration || migration.duration === 0) {
      const statisticsEnded = migration.statistics_obj?.ended;

      const isInProgressStatus =
        migration.status === MigrationStatus.in_progress ||
        migration.status === MigrationStatus.migrating;

      effectiveEnded = isInProgressStatus
        ? new Date().toISOString()
        : statisticsEnded || this.getLatestStepEndedFromRows(migration);

      effectedStarted =
        migration.started ||
        migration.statistics_obj?.steps?.find(
          (step) => step.name === MigrationSteps.CopyMetadata,
        )?.started ||
        migration.statistics_obj?.started ||
        "";

      migration.duration = this.getDurationInMilliseconds(
        effectedStarted,
        effectiveEnded,
      );
    } else {
      // Backend provided duration, but we still need effectedStarted and effectiveEnded for time display
      const statisticsEnded = migration.statistics_obj?.ended;

      const isInProgressStatus =
        migration.status === MigrationStatus.in_progress ||
        migration.status === MigrationStatus.migrating;

      effectiveEnded = isInProgressStatus
        ? new Date().toISOString()
        : statisticsEnded || this.getLatestStepEndedFromRows(migration);

      effectedStarted =
        migration.started ||
        migration.statistics_obj?.steps?.find(
          (step) => step.name === MigrationSteps.CopyMetadata,
        )?.started ||
        migration.statistics_obj?.started ||
        "";
    }

    migration.durationText = this.getDurationText(migration.duration);
    migration.statistics_started_local = this.getDateFromIsoString(
      effectedStarted,
      false,
      "Z",
    );
    migration.started_local = this.getDateFromIsoString(
      migration.started || effectedStarted,
      false,
      "Z",
    );
    migration.statistics_ended_local = this.getDateFromIsoString(
      effectiveEnded,
      false,
      "Z",
    );
    migration.statusCss = this.getClassFromMigrationStatus(migration.status);
    migration.postProcessStatusCss =
      this.getClassFromMigrationPostProcessingStatus(
        migration.post_process_status,
      );

    migration.created_dt_local = this.getDateFromIsoString(
      migration.created_dt as string,
      false,
      //"-08:00", // DB is stored in UTC even though db is in  PST (UTC-8)
    ).toString();

    // Normalize to ISO 8601 with PST offset for correct sorting
    migration.created_dt = this.normalizeDateToIso8601(
      migration.created_dt as string,
      //"-08:00", // DB is stored in UTC even though db is in  PST (UTC-8)
    );
    try {
      migration.progressPercentage = parseFloat(
        this.utilsService.trimEndChar(migration.progress.split(" ")[0], "%"),
      );
      migration.progressPercentageLabel = `${migration.progressPercentage}%`;
      migration.progressLabel =
        migration.progress?.split("%")[1].split(" - ")[0]?.replace("-", "") ||
        "";
      migration.statusLabel = migration.progress
        ?.split("%")[1]
        .split(" - ")[1]
        ?.replace("-", "");
      migration.exception = migration.progress
        ?.split("%")[1]
        .replace(" - ", "");
    } catch (e) {
      console.log(`processMigrationRecord error: ${e}`);
    }

    return migration;
  }

  processMigrationStatisticsRows(
    migration: Migration | null,
    sourceTimezoneOffset: string = "Z",
  ): MigrationStatisticsRow[] {
    const steps = migration?.statistics_obj?.steps || [];
    const rows: MigrationStatisticsRow[] = [];

    if (migration?.statistics) {
      // first add a row to capture the total execution
      rows.push({
        name: "Total execution",
        protocol_id: "",
        status: migration?.status || "",
        started: this.getDateFromIsoString(
          migration?.statistics_obj?.started || "",
          false,
          sourceTimezoneOffset,
        ),
        ended: this.getDateFromIsoString(
          migration?.statistics_obj?.ended || "",
          false,
          sourceTimezoneOffset,
        ),
        duration: this.getDurationInMilliseconds(
          migration?.statistics_obj?.started,
          migration?.statistics_obj?.ended,
        ),
        durationText: this.getDurationText(
          this.getDurationInMilliseconds(
            migration?.statistics_obj?.started,
            migration?.statistics_obj?.ended,
          ),
        ),
        description: "",
        startedSortValue: migration?.statistics_obj?.started || "",
        endedSortValue: migration?.statistics_obj?.ended || "",
      });
    }

    steps.forEach((step: MigrationExecutionStep) => {
      if (step.store_procedures && step.store_procedures.length > 0) {
        step.store_procedures.forEach(
          (procedure: MigrationExecutionStoredProcedure) => {
            rows.push({
              name: procedure.name,
              protocol_id: procedure.protocol_id,
              status: procedure.status,
              started: this.getDateFromIsoString(
                procedure.started,
                false,
                sourceTimezoneOffset,
              ),
              ended: this.getDateFromIsoString(
                procedure.ended,
                false,
                sourceTimezoneOffset,
              ),
              duration: this.getDurationInMilliseconds(
                procedure.started,
                procedure.ended,
              ),

              durationText: this.getDurationText(
                this.getDurationInMilliseconds(
                  procedure.started,
                  procedure.ended,
                ),
              ),
              description: "",
              startedSortValue: procedure.started || "",
              endedSortValue: procedure.ended || "",
            });
          },
        );
        return;
      }

      rows.push({
        name: step.name,
        protocol_id: "",
        status: step.status,
        started: this.getDateFromIsoString(
          step.started,
          false,
          sourceTimezoneOffset,
        ),
        ended: this.getDateFromIsoString(
          step.ended,
          false,
          sourceTimezoneOffset,
        ),
        duration: this.getDurationInMilliseconds(step.started, step.ended),
        durationText: this.getDurationText(
          this.getDurationInMilliseconds(step.started, step.ended),
        ),
        description: step.description || "",
        startedSortValue: step.started || "",
        endedSortValue: step.ended || "",
      });
    });

    return this.utilsService.sortArrayByDate(rows, "startedSortValue", "asc");
  }

  formatDateForDisplay(
    dateText: string | null | undefined,
    sourceTimezoneOffset: string = "Z",
  ): string {
    if (!dateText) {
      return "";
    }

    return this.getDateFromIsoString(dateText, false, sourceTimezoneOffset);
  }

  groupMigrationMetricsByEntityType(
    metrics: MigrationMetric[],
  ): GroupedMigrationMetrics {
    const grouped: GroupedMigrationMetrics = {};

    metrics.forEach((metric) => {
      // Initialize the entity type array if it doesn't exist
      if (!grouped[metric.entity_type]) {
        grouped[metric.entity_type] = [];
      }
      metric.migration_status = metric.migration_status
        ? metric.migration_status
        : MigrationStatus.unknown;
      // Add the metric to the array for this entity type
      metric.icon = this.getTableNameIcon(metric.entity_type);
      metric.displayData = this.getMigrationStatusDisplay(
        metric.migration_status,
      );
      grouped[metric.entity_type].push(metric);
    });

    return grouped;
  }

  processOverallMetrics(
    groupedMetrics: GroupedMigrationMetrics,
  ): OverallMetrics {
    let entityTypes = Object.keys(groupedMetrics);

    let overallMetrics = new OverallMetrics();

    entityTypes.forEach((key) => {
      groupedMetrics[key].forEach((metric) => {
        overallMetrics.totalRecords += metric.count;
        if (metric.migration_status != MigrationStatus.exception) {
          overallMetrics.succeeded += metric.count;
        } else {
          overallMetrics.failed += metric.count;
        }
      });
    });
    return overallMetrics;
  }

  /* MODALS*/
  rejectShowMigrationModal(
    company_name: string | undefined,
    onConfirm: (textFieldValue?: string) => void,
    onCancel: () => void,
  ) {
    this.dialogService.openConfirmDialog({
      enableTextField: true,
      mandatoryTextField: true,
      minlengthTextField: 5,
      rows: 3,
      title: "Reject company migration",
      message: "Are you sure you want to reject the company?",
      additionalMessage: company_name || "",
      confirmText: "Reject",
      cancelText: "Cancel",
      onConfirm: (comments) => onConfirm(comments),
      onCancel: () => onCancel(),
      confirmTextStyle: "warning-btn",
      textFieldLabel: "Comments:",
      textFieldPlaceholder:
        "Please add in a comment for a reason for rejection",
    });
  }

  public publishMigrationModal(config: {
    company_name?: string | undefined;
    onConfirm?: () => void;
    onCancel?: () => void;
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
  }) {
    const {
      company_name = "",
      onConfirm = () => {},
      onCancel = () => {},
      title = "Queue Migration",
      message = "Are you sure you want to queue migration for company?",
      confirmText = "Queue",
      cancelText = "Cancel",
    } = config;

    this.dialogService.openConfirmDialog({
      title: title,
      message: message,
      additionalMessage: `${company_name}`,
      confirmText: confirmText,
      cancelText: cancelText,
      onConfirm: () => onConfirm(),
      onCancel: () => onCancel(),
      confirmTextStyle: "success-btn",
    });
  }

  public remigrateShowMigrationModal(
    company_name: string | undefined,
    onConfirm: (textFieldValue?: string) => void,
    onCancel: () => void,
    ignoredProtocolIds: number[] = [],
    protocolsToRemigrate: number[] = [],
    selectedSiteIrisIds: string[] = [],
    missingProtocolIds: number[] = [],
  ) {
    const protocolsToRemigrateMessage =
      protocolsToRemigrate.length > 0
        ? `\nYou have chosen the following protocols to remigrate:\n ${protocolsToRemigrate.join(", ")}.`
        : "";

    const selectedSitesMessage =
      selectedSiteIrisIds.length > 0
        ? `\nYou have chosen the following sites to remigrate:\n ${selectedSiteIrisIds.join(", ")}.`
        : "";

    const ignoredProtocolsMessage =
      ignoredProtocolIds.length > 0
        ? `\nThe following protocol ids will be ignored, as they were migrated by other company:\n ${ignoredProtocolIds.join(", ")}.`
        : "";

    const missingProtocolsMessage =
      missingProtocolIds.length > 0
        ? `\nFor the following protocols there were no sites selected:\n ${missingProtocolIds.join(", ")}.`
        : "";

    this.dialogService.openConfirmDialog({
      enableTextField: true,
      mandatoryTextField: true,
      minlengthTextField: 5,
      rows: 3,
      title: "Remigrate company migration",
      message:
        `<strong>Are you sure you want to remigrate the company?</strong>${protocolsToRemigrateMessage}` +
        selectedSitesMessage +
        ignoredProtocolsMessage +
        missingProtocolsMessage,
      additionalMessage: company_name || "",
      confirmText: "Remigrate",
      cancelText: "Cancel",
      onConfirm: (comments) => onConfirm(comments),
      onCancel: () => onCancel(),
      confirmTextStyle: "primary-btn",
      textFieldLabel: "Comments:",
      textFieldPlaceholder:
        "Please add in a comment for a reason for remigration",
    });
  }

  /***********  PRIVATE ******************/

  /* Mappings */
  private getMigrationStatusDisplay(
    status: MigrationStatus,
  ): MigrationStatusColorMap {
    switch (status) {
      case MigrationStatus.queued:
        return new MigrationStatusColorMap("accent", "queue", "blue");
      case MigrationStatus.migrating:
        return new MigrationStatusColorMap(
          "accent",
          "hourglass_empty",
          "orange",
        );
      case MigrationStatus.in_progress:
        return new MigrationStatusColorMap("primary", "sync", "orange");
      case MigrationStatus.migrated:
        return new MigrationStatusColorMap("success", "check_circle", "green");
      case MigrationStatus.exception:
        return new MigrationStatusColorMap("warn", "error", "red");
      case MigrationStatus.fixed:
        return new MigrationStatusColorMap("accent", "error", "red");
      default:
        return new MigrationStatusColorMap("unclear", "lens_blur", "unclear");
    }
  }

  /**
   * Returns an appropriate Material icon for each migration database table
   * @param table The MigrationDbTables enum value
   * @returns Material icon name as string
   */
  private getTableNameIcon(table: MigrationDbTables): string {
    switch (table) {
      case MigrationDbTables.STUDY:
        return "book";

      case MigrationDbTables.USER:
        return "person";

      case MigrationDbTables.ORGANIZATION:
        return "business";

      case MigrationDbTables.ORGANIZATION_STUDY:
        return "corporate_fare";

      case MigrationDbTables.CONTACT:
        return "contact_phone";

      case MigrationDbTables.PRINCIPAL_INVESTIGATOR:
        return "group_search";

      case MigrationDbTables.SITE:
        return "location_on";

      case MigrationDbTables.STUDY_CONTACT:
        return "contact_mail";

      case MigrationDbTables.ADDRESS:
        return "home";

      case MigrationDbTables.ADDRESS_ENTITY:
        return "location_city";

      case MigrationDbTables.WORKSPACE:
        return "workspaces";

      case MigrationDbTables.FACILITY:
        return "apartment";

      case MigrationDbTables.FACILITY_LOCATION_SUBMISSION:
        return "upload_file";

      case MigrationDbTables.SITE_FACILITY:
        return "domain";

      case MigrationDbTables.DOCUMENT_TYPE:
        return "category";

      case MigrationDbTables.ORGANIZATION_TYPE:
        return "business_center";

      case MigrationDbTables.DOCUMENT:
        return "description";

      case MigrationDbTables.WORKSPACE_REVIEW:
        return "rate_review";

      case MigrationDbTables.REVIEW_ENTITY:
        return "reviews";

      case MigrationDbTables.REVIEW_HOLD:
        return "pause_circle";

      default:
        return "table_chart"; // Default icon for any unhandled cases
    }
  }

  /**
   * Normalizes backend date string to ISO 8601 format
   * Converts space separator to T for consistent parsing
   * @param dateText Raw date string from backend (e.g., '2026-03-08 03:25:24.4586100')
   * @param sourceTimezoneOffset UTC offset of the source data (e.g., '-08:00' for PST, '-07:00' for PDT, 'Z' for UTC). Defaults to 'Z'.
   * @returns ISO 8601 formatted string (e.g., '2026-03-08T03:25:24.4586100-08:00')
   */
  public normalizeDateToIso8601(
    dateText: string,
    sourceTimezoneOffset: string = "Z",
  ): string {
    if (!dateText) return "";

    // Replace space with T for ISO 8601 compliance
    let normalizedDate = dateText.replace(" ", "T");

    // If the date string doesn't have timezone info, append the source timezone offset
    if (
      !normalizedDate.includes("Z") &&
      !normalizedDate.includes("+") &&
      !normalizedDate.includes("-", 10)
    ) {
      normalizedDate = normalizedDate + sourceTimezoneOffset;
    }

    return normalizedDate;
  }

  private getDateFromIsoString(
    dateText: string,
    showMilliseconds: boolean = false,
    sourceTimezoneOffset: string = "Z",
  ): string {
    if (!dateText) return "";

    // Normalize to ISO 8601 format with the source timezone
    const dateString = this.normalizeDateToIso8601(
      dateText,
      sourceTimezoneOffset,
    );

    // Get the user's time zone
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return formatDate(
      new Date(dateString),
      `MMM d, y, h:mm:ss${showMilliseconds ? ".SSS" : ""} a`,
      this.locale,
      userTimeZone, // Explicitly pass the time zone
    );
  }

  private getClassFromMigrationStatus(status: MigrationStatus): string {
    switch (status) {
      case MigrationStatus.requeued:
      case MigrationStatus.queued:
        return "mat-success-light";
      case MigrationStatus.fixed:
        return "mat-success";

      case MigrationStatus.migrated:
        return "mat-success";

      case MigrationStatus.exception:
      case MigrationStatus.fk_exception:
        return "mat-error";
      case MigrationStatus.reject:
        return "mat-warning";
      case MigrationStatus.double_migrated:
        return "mat-warning";
      case MigrationStatus.staged:
        return "primary";
      case MigrationStatus.migrated_post_processing_failed:
        return "mat-partial-success";
      default:
        return "";
    }
  }
  private getClassFromMigrationPostProcessingStatus(
    status: MigrationPostProcessingStatus,
  ): string {
    switch (status) {
      case MigrationPostProcessingStatus.pending:
        return "mat-primary-light";
      case MigrationPostProcessingStatus.in_progress:
        return "mat-success-light";
      case MigrationPostProcessingStatus.completed:
        return "mat-success";
      case MigrationPostProcessingStatus.failed:
        return "mat-error";
      default:
        return "";
    }
  }

  private parseMigrationStatistics(statisticsText: string | null | undefined) {
    const emptyStatistics = { steps: [], started: "", ended: "" };

    if (!statisticsText) {
      return emptyStatistics;
    }

    try {
      return JSON.parse(statisticsText);
    } catch {
      return emptyStatistics;
    }
  }

  private getDurationInMilliseconds(
    started: string | null | undefined,
    ended: string | null | undefined,
  ): number {
    if (!started || !ended) {
      return 0;
    }

    const startedMs = new Date(started).getTime();
    const endedMs = new Date(ended).getTime();

    if (Number.isNaN(startedMs) || Number.isNaN(endedMs)) {
      return 0;
    }

    return Math.max(0, endedMs - startedMs);
  }

  public getDurationText(durationMs: number): string {
    if (!durationMs || durationMs <= 0) {
      return "0ms";
    }

    if (durationMs < 1000) {
      return `${Math.floor(durationMs)}ms`;
    }

    const totalSeconds = Math.floor(durationMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      const parts: string[] = [`${hours}h`];
      if (minutes > 0) {
        parts.push(`${minutes}m`);
      }
      if (seconds > 0) {
        parts.push(`${seconds}s`);
      }
      return parts.join(" ");
    }

    if (minutes > 0) {
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }

    return `${seconds}s`;
  }

  private updateEmptyFields(dataObject: any, ignoreProps: string[] = []) {
    for (const key in dataObject) {
      if (!ignoreProps.includes(key)) {
        if (dataObject[key] === "" || dataObject[key] === null) {
          dataObject[key] = "N/A";
        }
      }
    }
  }

  private getLatestStepEndedFromRows(migration: Migration): string {
    const rows = this.processMigrationStatisticsRows(migration, "Z");

    let latestEnded = "";
    let latestEndedMs = -1;

    rows.forEach((row) => {
      if (!row.endedSortValue) {
        return;
      }

      const endedMs = new Date(
        this.normalizeDateToIso8601(row.endedSortValue, "Z"),
      ).getTime();

      if (Number.isNaN(endedMs) || endedMs <= latestEndedMs) {
        return;
      }

      latestEndedMs = endedMs;
      latestEnded = row.endedSortValue;
    });

    return latestEnded;
  }

  /**
   * Open a study detail page in ERM based on int_id
   */
  openStudyDetailUrl(intId: number | string): void {
    if (!intId) {
      return;
    }
    const ermUrl = environment.ermUrl;
    if (ermUrl) {
      window.open(`${ermUrl}/studies/${intId}/details/overview`, "_blank");
    }
  }

  /**
   * Open a site detail page in ERM based on int_id
   */
  openSiteDetailUrl(intId: number | string): void {
    if (!intId) {
      return;
    }
    const ermUrl = environment.ermUrl;
    if (ermUrl) {
      window.open(`${ermUrl}/sites/${intId}/details/overview`, "_blank");
    }
  }
}
